// The Space's online presence as the server describes it (`WebsitePresence`): the Space header strip (design "H3":
// host + chip, View site, and ONE primary action) and the Online presence card's go-professional checklist (design
// "B": a progress bar and one row per step). The server decides every kind, state and url; this module only turns
// them into what to draw. Pure and unit-tested (`test/presence-checklist.test.ts`), with the SAME rules and the same
// keys on both phones (iOS `WebsitePresence.swift`, Android `WebsitePresence.kt`) — change one, change all three.
//
// `presence` is the SPACE's: `WorkspaceSummary.presence` (ListWorkspaces → the header, `headerPresence`) and
// `ListSpaceBindingsResponse.presence` (→ the card, `cardPresence`). An older server carried it only on
// `WorkspaceWebsite.presence` and the main WEBSITE `SpaceResource.presence`, which are read as the fallback. With
// none of them, both functions answer null and the callers keep today's rendering (`lib/presence.ts`). Enum values this build has never heard of
// are dropped, never guessed at.
//
// orion-web's client bundle must not import common-lib, so the wire shape below is a client-local copy of
// symphony-bff's `WebsitePresence`, exactly as /api/workspaces and /api/space-bindings pass it through.

import type { I18nCopy, SpaceBindingsResponse } from './space-resources';
import { metricTrend, stripVisits } from './site-analytics';

/* -------------------------------------------------------------------------------------------- wire shape */

export interface WebsitePresenceEdit {
  /** EDITOR_URL · CHAT · PLATFORM_ADMIN · NO_EDIT · TAKEOVER · MOVE. Anything else is NO_EDIT. */
  mode?: string;
  url?: string;
  /** The editor needs a computer (Wix's editors). Only the phones act on it. */
  desktopOnly?: boolean;
  /** CHAT only: the site's LIVE https address ('' while never published) — the preview pane and the review. */
  previewUrl?: string;
  /** MOVE only: the platform's own admin (wp-admin, Webflow's dashboard), a secondary link ('' when unknown). */
  adminUrl?: string;
}

export interface WebsitePresenceStep {
  /** BUILD · PUBLISH · DOMAIN · PREMIUM · EMAIL · BADGE · CONNECT · REBUILD · LINKED. */
  kind?: string;
  /** DONE · NEXT · TODO · LOCKED. */
  state?: string;
  url?: string;
}

export interface WebsitePresence {
  displayName?: string;
  /** WIX_STUDIO · WIX_EDITOR · WIX_NEW_EDITOR · WIX_HEADLESS · WORDPRESS · SHOPIFY · SQUARESPACE · WEBFLOW · OTHER_PLATFORM. */
  platform?: string;
  builtBySymphony?: boolean;
  edit?: WebsitePresenceEdit;
  dashboardUrl?: string;
  displayHost?: string;
  thumbnailUrl?: string;
  premium?: boolean;
  domainConnected?: boolean;
  suggestedDomain?: string;
  steps?: WebsitePresenceStep[];
  /** `state`: NOT_STARTED · AUDITED · PREVIEW_READY · SWITCHED. */
  migration?: { state?: string; previewUrl?: string };
  /** The Wix plan's name ("Business"); empty on a free plan, or when it could not be read (then "Premium"). */
  planName?: string;
  /** `state`: UNKNOWN_EMAIL_STATE · NO_EMAIL · WIX_MAILBOX · EXTERNAL_MAILBOX. UNKNOWN/unset shows nothing. */
  businessEmail?: { state?: string; address?: string };
  /** A domain the account owns, connected to no site (clayember.com); the DOMAIN step then connects it. */
  ownedDomain?: string;
  /** Wix analytics sessions; `known: false` = could not be read (no Visits cell, never zero). */
  visits?: { known?: boolean; sessions30d?: number | string; sessionsPrev30d?: number | string; dailySessions?: (number | string)[] };
  /** Where each strip cell's link opens; an empty one is not offered. */
  links?: { viewUrl?: string; managePlanUrl?: string; manageDomainUrl?: string; inboxUrl?: string; analyticsUrl?: string };
}

/* ------------------------------------------------------------------------------------------------ basics */

const P = 'presence.';

export type PresenceStepKind = 'BUILD' | 'PUBLISH' | 'DOMAIN' | 'PREMIUM' | 'EMAIL' | 'BADGE' | 'CONNECT' | 'REBUILD' | 'LINKED';
export type PresenceStepState = 'DONE' | 'NEXT' | 'TODO' | 'LOCKED';

const STEP_KINDS = new Set<string>(['BUILD', 'PUBLISH', 'DOMAIN', 'PREMIUM', 'EMAIL', 'BADGE', 'CONNECT', 'REBUILD', 'LINKED']);
const STEP_STATES = new Set<string>(['DONE', 'NEXT', 'TODO', 'LOCKED']);
const EXTERNAL = new Set(['WORDPRESS', 'SHOPIFY', 'SQUARESPACE', 'WEBFLOW', 'OTHER_PLATFORM']);

/** Only an http(s) address is ever opened; anything else (empty, `javascript:`, garbage) is no link. */
function href(url: string | undefined): string {
  const raw = (url ?? '').trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? raw : '';
  } catch {
    return '';
  }
}

/** A Wix platform: Studio, the classic editor, the new editor, or headless (what Symphony builds). */
export function presenceIsWix(platform: string | undefined): boolean {
  return (platform ?? '').startsWith('WIX_');
}

/** The platform's display name. Brand names are proper nouns and are not translated; OTHER/unknown have none. */
export function presencePlatformName(platform: string | undefined): string {
  if (presenceIsWix(platform)) return 'Wix';
  switch (platform) {
    case 'WORDPRESS':
      return 'WordPress';
    case 'SHOPIFY':
      return 'Shopify';
    case 'SQUARESPACE':
      return 'Squarespace';
    case 'WEBFLOW':
      return 'Webflow';
    default:
      return '';
  }
}

/** What the caller knows that `WebsitePresence` does not carry. */
export interface PresenceContext {
  /** The Space's name: the fallback for `displayName`. */
  spaceName: string;
  /** The site's public address (the tile's `url`, else the main WEBSITE row's `openUrl`), or '' when it has none. */
  siteUrl: string;
  /**
   * The reader is on a PHONE (`useIsPhone`: a coarse pointer AND a narrow viewport). Wix's Editor and Studio do not
   * work in a phone browser, so there a desktop-only editor link becomes the `desktopOnly` notice. Absent = a computer.
   */
  phone?: boolean;
  /**
   * The chat can host the site preview pane beside it (a wide, un-stacked layout). A computer whose chat is a
   * full-screen sheet (a narrow desktop window) gets the PHONE behaviour for a Symphony site: the chat alone, plus
   * "Review site". Absent = it can.
   */
  paneFits?: boolean;
}

/** The notice a `desktopOnly` target shows when tapped: "Editing is available only on desktop." */
export const PRESENCE_DESKTOP_ONLY_NOTICE = 'presence.edit.desktopOnly';

/**
 * Where an action goes. `url` opens in a new tab; `chat` opens the Space's chat seeded with `message` (the caller adds
 * the hidden context: the site's when it has one, the Space's otherwise); `desktopOnly` is a Wix Editor / Studio link
 * on a phone — the button looks disabled and a tap shows `message` ("Editing is available only on desktop.") instead
 * of opening an editor that cannot run there.
 */
export type PresenceTarget =
  | { kind: 'url'; url: string }
  | { kind: 'chat'; message: I18nCopy; site?: SiteChat }
  | { kind: 'desktopOnly'; message: I18nCopy }
  /** A Wix Headless site built elsewhere: the hand-over offer (`TakeoverOffer`). */
  | { kind: 'takeover' }
  /** WordPress, Webflow, Squarespace, other: the move-to-Symphony offer (`MoveOffer`). */
  | { kind: 'move'; offer: SiteMoveOffer }
  /** A phone's "Review site": the in-app review of `previewUrl` (`SiteReviewSheet`). */
  | { kind: 'review'; previewUrl: string }
  /** The strip's Visits tile: the Space's in-app Analytics page (`/workspace/<slug>/analytics`) — never Wix. */
  | { kind: 'analytics' }
  /** Upgrade (a free Wix site): the Space's in-app Plans page (`/workspace/<slug>/plan`) — never Wix's plan picker. */
  | { kind: 'plans' }
  /** Get a domain (a Wix site with none of its own): the in-app Domain page (`/workspace/<slug>/domain`). */
  | { kind: 'domain' };

/** What a Symphony site's Edit chat carries besides its seed: the live address, and whether the pane opens beside it. */
export interface SiteChat {
  previewUrl: string;
  pane: boolean;
}

export type SiteDevice = 'phone' | 'computer';

export interface SiteMoveOffer {
  /** `start` (not started / audited / unknown), `compare` (preview ready), `done` (switched). */
  state: 'start' | 'compare' | 'done';
  /** `compare` only: the migrated site to compare. */
  compareUrl: string;
  /** The platform's own admin, a secondary "Edit on {platform}" link; '' for none. */
  adminUrl: string;
  /** The platform's display name, '' for an unnamed platform. */
  platformName: string;
}

/** Where Edit goes (contract §1), by device. Null = no Edit button. */
export type SiteEditAction =
  | { kind: 'openUrl'; url: string }
  | { kind: 'desktopOnly' }
  | { kind: 'chat'; previewUrl: string; pane: boolean }
  | { kind: 'takeover' }
  | ({ kind: 'move' } & SiteMoveOffer);

export interface PresenceAction {
  label: I18nCopy;
  target: PresenceTarget;
  /** The page-click BI button name. */
  bi: string;
}

function name(presence: WebsitePresence, ctx: PresenceContext): string {
  return (presence.displayName ?? '').trim() || ctx.spaceName.trim();
}

/** The site's address for a seed: its url, else its name (a site not live yet may have none). */
function siteRef(presence: WebsitePresence, ctx: PresenceContext): string {
  return ctx.siteUrl.trim() || name(presence, ctx);
}

/** The known steps, in the server's order: unknown kinds and states are dropped. */
function knownSteps(presence: WebsitePresence): { kind: PresenceStepKind; state: PresenceStepState; url: string }[] {
  return (presence.steps ?? [])
    .filter((s) => STEP_KINDS.has(s.kind ?? '') && STEP_STATES.has(s.state ?? ''))
    .map((s) => ({ kind: s.kind as PresenceStepKind, state: s.state as PresenceStepState, url: href(s.url) }));
}

function chat(key: string, values?: Record<string, string>): PresenceTarget {
  return { kind: 'chat', message: values ? { key, values } : { key } };
}

/** The server marked the editor desktop-only (Wix Editor / Studio) and the reader is on a phone. */
function editorBlockedHere(presence: WebsitePresence, ctx: PresenceContext): boolean {
  return ctx.phone === true && presence.edit?.mode === 'EDITOR_URL' && presence.edit.desktopOnly === true;
}

/**
 * A url target, or — on a phone, for the desktop-only editor's own link (Edit, and a PUBLISH step that opens the same
 * editor) — the `desktopOnly` notice. Null for no url.
 */
function urlTarget(u: string, presence: WebsitePresence, ctx: PresenceContext): PresenceTarget | null {
  if (!u) return null;
  if (editorBlockedHere(presence, ctx) && u === href(presence.edit?.url)) return { kind: 'desktopOnly', message: { key: PRESENCE_DESKTOP_ONLY_NOTICE } };
  return { kind: 'url', url: u };
}

/**
 * A step's action, or null when it has none (LINKED), or when its target is a url the server did not send.
 * BUILD, EMAIL, BADGE and REBUILD (until its preview is ready) are chats; on a Wix site PREMIUM opens the in-app Plans
 * page and DOMAIN the in-app Domain page (an owned domain keeps its connect url), elsewhere they open their url; PUBLISH and
 * CONNECT open their url when there is one and fall back to a chat.
 */
function stepAction(
  step: { kind: PresenceStepKind; url: string },
  presence: WebsitePresence,
  ctx: PresenceContext,
): PresenceAction | null {
  const bi = `presence_step_${step.kind.toLowerCase()}`;
  const url = (u: string): PresenceTarget | null => urlTarget(u, presence, ctx);
  const make = (label: I18nCopy, target: PresenceTarget | null) => (target ? { label, target, bi } : null);
  const platform = presencePlatformName(presence.platform);
  const domain = (presence.suggestedDomain ?? '').trim();
  const wix = presenceIsWix(presence.platform);
  switch (step.kind) {
    case 'BUILD':
      return make({ key: `${P}step.BUILD.action` }, chat(`${P}prompt.build`, { name: name(presence, ctx) }));
    case 'PUBLISH':
      return make({ key: `${P}step.PUBLISH.action` }, url(step.url) ?? chat(`${P}prompt.publish`, { url: siteRef(presence, ctx) }));
    case 'DOMAIN':
      // A Wix site with no domain of its own gets the in-app Domain page (spec 2026-10-25, plan-domain); an OWNED
      // domain keeps the server's connect link.
      return make(
        domain ? { key: `${P}step.DOMAIN.actionSuggested`, values: { domain } } : { key: `${P}step.DOMAIN.action` },
        wix && !(presence.ownedDomain ?? '').trim() ? { kind: 'domain' } : url(step.url),
      );
    case 'PREMIUM':
      return make({ key: `${P}step.PREMIUM.action` }, wix ? { kind: 'plans' } : url(step.url));
    case 'EMAIL':
      return make({ key: `${P}step.EMAIL.action` }, chat(`${P}prompt.email`));
    case 'BADGE':
      return make({ key: `${P}step.BADGE.action` }, chat(`${P}prompt.badge`, { url: siteRef(presence, ctx) }));
    case 'CONNECT':
      return make({ key: `${P}step.CONNECT.action` }, url(step.url) ?? chat(`${P}prompt.connect`, { platform }));
    case 'REBUILD':
      if (presence.migration?.state === 'PREVIEW_READY') return make({ key: `${P}step.REBUILD.actionCompare` }, url(href(presence.migration.previewUrl)));
      return make({ key: `${P}step.REBUILD.action` }, chat(`${P}prompt.rebuild`, { url: siteRef(presence, ctx) }));
    case 'LINKED':
    default:
      return null;
  }
}

/** A url action (or its desktop-only stand-in), which a TODO row shows as a small secondary button; a TODO chat action shows none. */
function isUrl(action: PresenceAction | null): boolean {
  // The in-app Plans / Domain pages are a destination like a url, so a TODO row keeps its small button for them too.
  return action?.target.kind === 'url' || action?.target.kind === 'desktopOnly' || action?.target.kind === 'plans' || action?.target.kind === 'domain';
}

/* ------------------------------------------------------------------------------------------------- Edit */

/**
 * Where Edit goes, by device — the one decision, the same on iOS (`SiteEditRules.action`) and Android
 * (`siteEditAction`). `device` decides the desktop-only editor; `paneDevice` (default `device`) decides whether a
 * Symphony site's chat opens with its preview pane — web passes 'phone' there for a computer whose chat cannot host a
 * pane. An unknown mode (a future enum value) is NO_EDIT.
 */
export function siteEditAction(presence: WebsitePresence, device: SiteDevice, paneDevice: SiteDevice = device): SiteEditAction | null {
  const edit = presence.edit;
  const url = href(edit?.url);
  switch (edit?.mode) {
    case 'EDITOR_URL':
      if (!url) return null;
      return device === 'phone' && edit.desktopOnly === true ? { kind: 'desktopOnly' } : { kind: 'openUrl', url };
    case 'CHAT': {
      const previewUrl = href(edit.previewUrl);
      return { kind: 'chat', previewUrl, pane: previewUrl !== '' && paneDevice === 'computer' };
    }
    case 'PLATFORM_ADMIN':
      return url ? { kind: 'openUrl', url } : null;
    case 'TAKEOVER':
      return { kind: 'takeover' };
    case 'MOVE': {
      const migration = presence.migration;
      const compareUrl = migration?.state === 'PREVIEW_READY' ? href(migration.previewUrl) : '';
      const state = migration?.state === 'SWITCHED' ? 'done' : compareUrl ? 'compare' : 'start';
      return { kind: 'move', state, compareUrl, adminUrl: href(edit.adminUrl), platformName: presencePlatformName(presence.platform) };
    }
    default:
      return null;
  }
}

/** A phone's "Review site": the CHAT site's live address, else null. On a computer the review runs in the pane. */
export function siteReviewEntry(presence: WebsitePresence | null | undefined, device: SiteDevice): string | null {
  if (device !== 'phone' || presence?.edit?.mode !== 'CHAT') return null;
  return href(presence.edit.previewUrl) || null;
}

function chatDevice(ctx: PresenceContext): SiteDevice {
  return ctx.phone === true || ctx.paneFits === false ? 'phone' : 'computer';
}

function reviewAction(presence: WebsitePresence, ctx: PresenceContext): PresenceAction | null {
  const previewUrl = siteReviewEntry(presence, chatDevice(ctx));
  return previewUrl ? { label: { key: 'siteReview.action.review' }, target: { kind: 'review', previewUrl }, bi: 'presence_review_site' } : null;
}

/**
 * The Edit line, or null (NO_EDIT, an unknown mode, or a url mode with no url), from `siteEditAction`:
 *   - EDITOR_URL → "Edit site", opens `edit.url` in a new tab — except on a PHONE (`ctx.phone`) when the server marked
 *     the editor `desktopOnly` (Wix Editor / Studio): then "Edit site" looks disabled and a tap shows "Editing is
 *     available only on desktop." (`desktopOnly` target). Never a move-to-Symphony offer, never a share;
 *   - CHAT → "Ask Symphony to change it", the Space chat seeded with the website tile's own edit prompt, carrying the
 *     site's live address (`site`) so the chat can show the preview link, and on a computer the preview pane;
 *   - PLATFORM_ADMIN → "Open {platform} admin" (or "Edit site" when the platform has no name), opens `edit.url`;
 *   - TAKEOVER → "Edit site", the hand-over offer; MOVE → "Edit site", the move offer.
 */
export function presenceEdit(presence: WebsitePresence, ctx: PresenceContext): PresenceAction | null {
  const action = siteEditAction(presence, ctx.phone === true ? 'phone' : 'computer', chatDevice(ctx));
  if (!action) return null;
  switch (action.kind) {
    case 'desktopOnly':
      return { label: { key: `${P}edit.inEditor` }, target: { kind: 'desktopOnly', message: { key: PRESENCE_DESKTOP_ONLY_NOTICE } }, bi: 'presence_edit_desktop_only' };
    case 'openUrl': {
      if (presence.edit?.mode !== 'PLATFORM_ADMIN') return { label: { key: `${P}edit.inEditor` }, target: { kind: 'url', url: action.url }, bi: 'presence_edit_editor' };
      const platform = presencePlatformName(presence.platform);
      return {
        label: platform ? { key: `${P}edit.platformAdmin`, values: { platform } } : { key: `${P}action.editSite` },
        target: { kind: 'url', url: action.url },
        bi: 'presence_edit_platform_admin',
      };
    }
    case 'chat': {
      const message: I18nCopy = { key: 'workspace.website.editPrompt', values: { url: siteRef(presence, ctx) } };
      return {
        label: { key: `${P}edit.askSymphony` },
        target: action.previewUrl ? { kind: 'chat', message, site: { previewUrl: action.previewUrl, pane: action.pane } } : { kind: 'chat', message },
        bi: 'presence_edit_chat',
      };
    }
    case 'takeover':
      return { label: { key: `${P}action.editSite` }, target: { kind: 'takeover' }, bi: 'presence_edit_takeover' };
    case 'move': {
      const { kind: _kind, ...offer } = action;
      return { label: { key: `${P}action.editSite` }, target: { kind: 'move', offer }, bi: 'presence_edit_move' };
    }
  }
}

/* ----------------------------------------------------------------------------------------------- header */

export type PresenceChipTone = 'free' | 'premium' | 'platform' | 'built';

export interface PresenceHeader {
  /** The address line: `displayHost`, or "Free Wix address" on a Wix site with none; null = no host line. */
  host: { text: string } | { key: string } | null;
  /** One chip after the host, or null. */
  chip: { label: I18nCopy | { text: string }; tone: PresenceChipTone } | null;
  /** "View site" (Shopify: "View store"), when the site has a public address. */
  view: PresenceAction | null;
  /** The first NEXT step's action — the checklist's own next step. */
  primary: PresenceAction | null;
  /** With no NEXT step: "Edit site", following `edit` (absent on NO_EDIT). */
  edit: PresenceAction | null;
  /** A phone's "Review site" (a Symphony site with a live address), whatever the primary action is. */
  review: PresenceAction | null;
}

/**
 * The header's chip, first match wins:
 *   built by Symphony → "Built by Symphony"; Wix and not Premium → "Free plan"; Premium without its own domain →
 *   "Premium"; an external platform with a name → that name. Otherwise none.
 */
function headerChip(presence: WebsitePresence): PresenceHeader['chip'] {
  if (presence.builtBySymphony === true) return { label: { key: `${P}chip.builtBySymphony` }, tone: 'built' };
  const wix = presenceIsWix(presence.platform);
  if (wix && presence.premium !== true) return { label: { key: `${P}chip.freePlan` }, tone: 'free' };
  if (presence.premium === true && presence.domainConnected !== true) return { label: { key: `${P}chip.premium` }, tone: 'premium' };
  const platform = presencePlatformName(presence.platform);
  if (EXTERNAL.has(presence.platform ?? '') && platform) return { label: { text: platform }, tone: 'platform' };
  return null;
}

/** The Space header strip (H3), or null when the server sent no `presence` (keep today's line). */
export function presenceHeader(presence: WebsitePresence | null | undefined, ctx: PresenceContext): PresenceHeader | null {
  if (!presence) return null;
  const host = (presence.displayHost ?? '').trim();
  const site = href(ctx.siteUrl);
  const next = knownSteps(presence)
    .filter((s) => s.state === 'NEXT')
    .map((s) => stepAction(s, presence, ctx))
    .find((a): a is PresenceAction => a !== null);
  const edit = presenceEdit(presence, ctx);
  return {
    host: host ? { text: host } : presenceIsWix(presence.platform) ? { key: `${P}host.freeWixAddress` } : null,
    chip: headerChip(presence),
    view: site
      ? { label: { key: presence.platform === 'SHOPIFY' ? `${P}action.viewStore` : `${P}action.viewSite` }, target: { kind: 'url', url: site }, bi: 'presence_view_site' }
      : null,
    primary: next ? { ...next, bi: `presence_header_${next.bi.replace('presence_step_', '')}` } : null,
    edit: next || !edit ? null : { ...edit, label: { key: `${P}action.editSite` } },
    review: reviewAction(presence, ctx),
  };
}

/* ------------------------------------------------------------------------------------------------- card */

export interface PresenceChecklistRow {
  kind: PresenceStepKind;
  state: PresenceStepState;
  title: I18nCopy;
  /** NEXT and TODO only. */
  detail: I18nCopy | null;
  /** NEXT: its action as the primary button; TODO: a small secondary one, only when it opens a url; else none. */
  action: PresenceAction | null;
  primary: boolean;
}

export interface PresenceCard {
  /** `displayName`, else the Space name — never a Wix internal handle. */
  name: string;
  /** The site's picture, or '' for the neutral placeholder. */
  thumbnailUrl: string;
  title: I18nCopy;
  done: number;
  total: number;
  rows: PresenceChecklistRow[];
  edit: PresenceAction | null;
  /** A phone's "Review site", beside Edit on the edit line. */
  review: PresenceAction | null;
  /** Wix platforms only: "Dashboard", opening `dashboardUrl`. */
  dashboard: PresenceAction | null;
}

function rowTitle(step: { kind: PresenceStepKind; state: PresenceStepState }, presence: WebsitePresence): I18nCopy {
  const domain = (presence.suggestedDomain ?? '').trim();
  switch (step.kind) {
    case 'PUBLISH':
      return { key: step.state === 'DONE' ? `${P}step.PUBLISH.title` : `${P}step.PUBLISH.titleNext` };
    case 'DOMAIN':
      return domain ? { key: `${P}step.DOMAIN.titleSuggested`, values: { domain } } : { key: `${P}step.DOMAIN.title` };
    case 'CONNECT':
      return { key: `${P}step.CONNECT.title`, values: { platform: presencePlatformName(presence.platform) } };
    case 'REBUILD':
      return { key: presence.migration?.state === 'PREVIEW_READY' ? `${P}step.REBUILD.titleReady` : `${P}step.REBUILD.title` };
    default:
      return { key: `${P}step.${step.kind}.title` };
  }
}

function rowDetail(kind: PresenceStepKind, presence: WebsitePresence): I18nCopy | null {
  if (kind === 'LINKED') return null;
  const domain = (presence.suggestedDomain ?? '').trim();
  if (kind === 'DOMAIN' && domain) return { key: `${P}step.DOMAIN.detailSuggested`, values: { domain } };
  return { key: `${P}step.${kind}.detail` };
}

/**
 * The checklist's title, first match wins: every step DONE → "You're set up professionally"; Shopify → "Let your
 * agents run the store"; another external platform → "Get more from your website"; a BUILD step not yet DONE → "Get
 * {name} online"; otherwise → "Look professional online".
 */
function cardTitle(presence: WebsitePresence, steps: { kind: PresenceStepKind; state: PresenceStepState }[], cardName: string): I18nCopy {
  if (steps.length > 0 && steps.every((s) => s.state === 'DONE')) return { key: `${P}checklist.title.done` };
  if (presence.platform === 'SHOPIFY') return { key: `${P}checklist.title.store` };
  if (EXTERNAL.has(presence.platform ?? '')) return { key: `${P}checklist.title.external` };
  // A BUILD step still to do: a DONE one is a site Symphony built (kind 3), which reads as the Wix title.
  if (steps.some((s) => s.kind === 'BUILD' && s.state !== 'DONE')) return { key: `${P}checklist.title.noWebsite`, values: { name: cardName } };
  return { key: `${P}checklist.title.wix` };
}

/** The Online presence card's checklist (B), or null when the server sent no `presence` (keep today's row). */
export function presenceCard(presence: WebsitePresence | null | undefined, ctx: PresenceContext): PresenceCard | null {
  if (!presence) return null;
  const steps = knownSteps(presence);
  const cardName = name(presence, ctx);
  const rows = steps.map<PresenceChecklistRow>((step) => {
    const action = stepAction(step, presence, ctx);
    const open = step.state === 'NEXT' || step.state === 'TODO';
    return {
      kind: step.kind,
      state: step.state,
      title: rowTitle(step, presence),
      detail: open ? rowDetail(step.kind, presence) : null,
      action: step.state === 'NEXT' ? action : step.state === 'TODO' && isUrl(action) ? action : null,
      primary: step.state === 'NEXT',
    };
  });
  const dashboardUrl = href(presence.dashboardUrl);
  return {
    name: cardName,
    thumbnailUrl: href(presence.thumbnailUrl),
    title: cardTitle(presence, steps, cardName),
    done: steps.filter((s) => s.state === 'DONE').length,
    total: steps.length,
    rows,
    edit: presenceEdit(presence, ctx),
    review: reviewAction(presence, ctx),
    dashboard:
      presenceIsWix(presence.platform) && dashboardUrl
        ? { label: { key: `${P}action.dashboard` }, target: { kind: 'url', url: dashboardUrl }, bi: 'presence_dashboard' }
        : null,
  };
}

/**
 * The `presence` the HEADER draws: the Space-level `WorkspaceSummary.presence` (ListWorkspaces), else the website
 * tile's `WorkspaceWebsite.presence` (an older server, which carried it only for an owned Wix site).
 *
 * The Space-level field is ALWAYS the Space's presence, and holds the same value as the tile's when both exist; it is
 * the only carrier for a WordPress / Shopify / plain-URL site and for a Space with no website yet. It is unset when the
 * owner said "no website", for a Personal Space with no site, when the Space has websites but none is main, and when
 * a main Wix site could not be read as the caller's — and then the header renders as it did before `presence`.
 */
export function headerPresence(
  workspace: { presence?: WebsitePresence; website?: { presence?: WebsitePresence } | null } | null | undefined,
): WebsitePresence | null {
  return workspace?.presence ?? workspace?.website?.presence ?? null;
}

/**
 * The `presence` the CARD draws: the Space-level `ListSpaceBindingsResponse.presence`, else the main WEBSITE row's (an
 * older server), else the header's (`spacePresence`, read from the Space list). Null renders the card as before.
 */
export function cardPresence(bindings: SpaceBindingsResponse | undefined, spacePresence: WebsitePresence | null | undefined): WebsitePresence | null {
  if (bindings?.presence) return bindings.presence;
  const main = (bindings?.resources ?? []).find((r) => r.kind === 'WEBSITE' && r.state !== 'CANDIDATE' && r.main === true && r.presence);
  return main?.presence ?? spacePresence ?? null;
}

/* ------------------------------------------------------------------------------------------------ strip */

export type StripCellKind = 'site' | 'plan' | 'domain' | 'email' | 'visits' | 'onWix';
export type StripTone = 'warning' | 'success' | 'danger' | 'neutral';

/** A strip action: a plain link, or — the ONE accent of the strip — the first NEXT step's action (`primary`). */
export interface StripAction extends PresenceAction {
  primary: boolean;
}

export interface StripCell {
  kind: StripCellKind;
  label: I18nCopy;
  /** The value: a key, display text as-is, a count the surface formats in its locale (Visits), or unknown (Visits whose
   * read failed: the surface draws an em dash). */
  value: I18nCopy | { text: string } | { count: number } | { unknown: true };
  /** Visits only: the last 30 days, oldest first (the sparkline); [] when the server sent none. */
  daily: number[];
  /** Site only: the small host under the name. */
  sub: string;
  /** Site only: the site's picture ('' = the globe). */
  thumbnailUrl: string;
  /** Plan only: the value is the premium badge (success tone, crown glyph). */
  badge: boolean;
  tone: StripTone;
  /** Visits only: the change against the 30 days before; null when the previous count is 0. */
  trend: { direction: 'up' | 'down' | 'flat'; percent: number } | null;
  /** At most one; a link unless it is the accent. */
  action: StripAction | null;
  /** Site only: the site's Edit, beside View site — `stripEditAction`, never the accent. */
  edit: StripAction | null;
  /** Holds the accent: spans both columns of the phone grid so its label is never truncated. */
  wide: boolean;
}

export interface PresenceStrip {
  cells: StripCell[];
  /** The accent when no drawn cell owns the first NEXT step (BUILD, PUBLISH, CONNECT, BADGE): after the cells. */
  trailingPrimary: StripAction | null;
  /** The outlined Manage button: opens the Online presence card. */
  manage: { label: I18nCopy; bi: string };
}

/** The cell that owns a step's action; a step no cell owns is the trailing primary. */
const STEP_CELL: Partial<Record<PresenceStepKind, StripCellKind>> = { DOMAIN: 'domain', PREMIUM: 'plan', EMAIL: 'email', REBUILD: 'onWix' };
const MAILBOX_STATES = new Set(['NO_EMAIL', 'WIX_MAILBOX', 'EXTERNAL_MAILBOX']);

/** The trend of `now` against `prev`, rounded to a whole percent; null when there is nothing to compare against. The
 * ONE rule the Analytics page uses too (`metricTrend`, pinned by the shared site-analytics vectors). */
export function visitsTrend(now: number, prev: number): StripCell['trend'] {
  return metricTrend(now, prev);
}

/**
 * The site strip's Edit, beside View site in the Site cell (contract §1: "the Space strip offers it"), or null. Built on
 * the card's own decision (`siteEditAction`, via `reviewAction` / `presenceEdit`) so the two cannot drift:
 *   - a CHAT site where the chat cannot host the pane (a phone, or a desktop window too narrow) and the site is live →
 *     "Review site" (the in-app review);
 *   - otherwise the card's Edit, labelled "Edit site": CHAT opens the site's chat (with the pane on a computer),
 *     EDITOR_URL opens the editor (a phone gets the desktop-only notice when the server marked it so), TAKEOVER and MOVE
 *     open their offers; PLATFORM_ADMIN keeps "Open {platform} admin";
 *   - NO_EDIT, an unknown mode, or a url mode with no url → null.
 */
export function stripEditAction(presence: WebsitePresence, ctx: PresenceContext): StripAction | null {
  const review = reviewAction(presence, ctx);
  if (review) return { ...review, bi: 'presence_strip_review_site', primary: false };
  const edit = presenceEdit(presence, ctx);
  if (!edit) return null;
  const label = presence.edit?.mode === 'PLATFORM_ADMIN' ? edit.label : { key: `${P}action.editSite` };
  return { ...edit, label, bi: edit.bi.replace('presence_', 'presence_strip_'), primary: false };
}

/**
 * The Space header's site strip, or null when the server sent no `presence` (keep today's header, minus the
 * description). Cells, in order, each drawn only when its condition holds: Site (always), Plan and Domain (a Wix
 * platform), Business email (Wix, own domain connected, and an email state that was read), Visits (`stripVisits`: known, or a
 * Wix site whose read failed),
 * On Wix (a REBUILD step). Exactly ONE accent, for the first NEXT step: in the cell that owns it (DOMAIN → Domain,
 * PREMIUM → Plan, EMAIL → Business email, REBUILD → On Wix) it is that CELL's own action (label and target) drawn as the
 * primary button; a NEXT step no drawn cell owns is `trailingPrimary`, with the step's own label. No NEXT step → no
 * accent at all.
 */
export function presenceStrip(presence: WebsitePresence | null | undefined, ctx: PresenceContext): PresenceStrip | null {
  if (!presence) return null;
  const steps = knownSteps(presence);
  const links = presence.links ?? {};
  const wix = presenceIsWix(presence.platform);
  const step = (kind: PresenceStepKind) => steps.find((s) => s.kind === kind);
  const stepUrl = (kind: PresenceStepKind) => step(kind)?.url ?? '';
  const link = (labelKey: string, u: string | undefined, bi: string, values?: Record<string, string>): StripAction | null => {
    const url = href(u);
    return url ? { label: values ? { key: labelKey, values } : { key: labelKey }, target: { kind: 'url', url }, bi, primary: false } : null;
  };
  const inApp = (labelKey: string, target: PresenceTarget, bi: string, values?: Record<string, string>): StripAction => ({
    label: values ? { key: labelKey, values } : { key: labelKey },
    target,
    bi,
    primary: false,
  });
  const cell = (kind: StripCellKind, labelKey: string, value: StripCell['value'], action: StripAction | null, extra: Partial<StripCell> = {}): StripCell => ({
    kind,
    label: { key: `${P}cell.${labelKey}` },
    value,
    sub: '',
    thumbnailUrl: '',
    badge: false,
    tone: 'neutral',
    trend: null,
    daily: [],
    action,
    edit: null,
    wide: false,
    ...extra,
  });
  const cells: StripCell[] = [];

  // Site — always.
  const noWebsite = steps.some((s) => s.kind === 'BUILD' && s.state !== 'DONE');
  if (noWebsite) cells.push(cell('site', 'site', { key: `${P}site.none` }, null));
  else {
    const view = link(presence.platform === 'SHOPIFY' ? `${P}action.viewStore` : `${P}action.viewSite`, href(links.viewUrl) || ctx.siteUrl, 'presence_strip_view_site');
    cells.push(
      cell('site', 'site', { text: name(presence, ctx) }, view, {
        sub: (presence.displayHost ?? '').trim(),
        thumbnailUrl: href(presence.thumbnailUrl),
        edit: stripEditAction(presence, ctx),
      }),
    );
  }

  if (wix) {
    // Plan.
    if (presence.premium === true) {
      const planName = (presence.planName ?? '').trim();
      cells.push(
        // Manage plan opens the in-app Plans page too (current plan, upgrades), never Wix's subscriptions page.
        cell('plan', 'plan', planName ? { text: planName } : { key: `${P}chip.premium` }, inApp(`${P}action.managePlan`, { kind: 'plans' }, 'presence_strip_manage_plan'), {
          badge: true,
          tone: 'success',
        }),
      );
    } else {
      cells.push(
        // Upgrade opens the in-app Plans page (spec 2026-10-25, plan-domain), never Wix's plan picker.
        cell('plan', 'plan', { key: `${P}plan.free` }, inApp(`${P}action.upgrade`, { kind: 'plans' }, 'presence_strip_upgrade'), { tone: 'warning' }),
      );
    }
    // Domain.
    const owned = (presence.ownedDomain ?? '').trim();
    const suggested = (presence.suggestedDomain ?? '').trim();
    if (presence.domainConnected === true) {
      // Manage domain opens the in-app Domain page (the connected-domain card), never Wix's domains page.
      cells.push(cell('domain', 'domain', { text: (presence.displayHost ?? '').trim() }, inApp(`${P}action.manageDomain`, { kind: 'domain' }, 'presence_strip_manage_domain')));
    } else if (owned) {
      cells.push(cell('domain', 'domain', { key: `${P}domain.owned`, values: { domain: owned } }, link(`${P}action.connect`, stepUrl('DOMAIN'), 'presence_strip_connect_domain')));
    } else {
      // Get a domain opens the in-app Domain page, never Wix's domain search.
      const get = suggested
        ? inApp(`${P}step.DOMAIN.actionSuggested`, { kind: 'domain' }, 'presence_strip_get_domain', { domain: suggested })
        : inApp(`${P}step.DOMAIN.action`, { kind: 'domain' }, 'presence_strip_get_domain');
      cells.push(cell('domain', 'domain', { key: `${P}host.freeWixAddress` }, get));
    }
    // Business email — only once read, and only on the site's own domain.
    const email = presence.businessEmail;
    if (presence.domainConnected === true && MAILBOX_STATES.has(email?.state ?? '')) {
      if (email?.state === 'NO_EMAIL') {
        const connect: StripAction = link(`${P}action.connect`, links.inboxUrl, 'presence_strip_connect_email') ?? {
          label: { key: `${P}action.connect` },
          target: chat(`${P}prompt.email`),
          bi: 'presence_strip_connect_email',
          primary: false,
        };
        cells.push(cell('email', 'email', { key: `${P}email.none` }, connect));
      } else {
        const address = (email?.address ?? '').trim();
        cells.push(cell('email', 'email', address ? { text: address } : { key: `${P}email.on` }, link(`${P}action.openMail`, links.inboxUrl, 'presence_strip_open_mail')));
      }
    }
  }

  // Visits — the whole tile opens the in-app Analytics page (spec 2026-10-25 §4); a failed read on a Wix site still
  // shows the tile, as "—", and the page shows its error state.
  const visits = stripVisits(presence.visits, wix);
  if (visits) {
    const trend = visits.trend;
    const open: StripAction = { label: { key: `${P}action.analytics` }, target: { kind: 'analytics' }, bi: 'presence_strip_analytics', primary: false };
    cells.push(
      cell('visits', 'visits', visits.known ? { count: visits.count } : { unknown: true }, open, {
        trend,
        daily: visits.daily,
        tone: trend?.direction === 'up' ? 'success' : trend?.direction === 'down' ? 'danger' : 'neutral',
      }),
    );
  }

  // On Wix — a REBUILD step.
  const rebuild = step('REBUILD');
  if (rebuild) {
    const ready = presence.migration?.state === 'PREVIEW_READY';
    const action = stepAction(rebuild, presence, ctx);
    cells.push(
      cell('onWix', 'onWix', { key: ready ? `${P}step.REBUILD.titleReady` : `${P}onWix.value` }, action ? { ...action, bi: 'presence_strip_rebuild', primary: false } : null),
    );
  }

  // The one accent.
  let trailingPrimary: StripAction | null = null;
  const next = steps
    .filter((s) => s.state === 'NEXT')
    .map((s) => ({ s, action: stepAction(s, presence, ctx) }))
    .find((n) => n.action !== null);
  if (next && next.action) {
    const bi = `presence_header_${next.s.kind.toLowerCase()}`;
    const owner = cells.find((c) => c.kind === STEP_CELL[next.s.kind]);
    if (owner) {
      // The cell's OWN action ("Connect", "Get {domain}", "Upgrade") drawn as the primary — as drawn and approved; the
      // step's action stands in only when the cell has none of its own.
      owner.action = { ...(owner.action ?? next.action), bi, primary: true };
      owner.wide = true;
    } else trailingPrimary = { ...next.action, bi, primary: true };
  }

  return { cells, trailingPrimary, manage: { label: { key: `${P}action.manage` }, bi: 'presence_manage' } };
}
